import React, { useEffect } from "react";
import { Drawer } from "../../../components/Drawer";
import { useTranslation } from "react-i18next";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Direction } from "../../../model/shared/Direction.enum";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { isDateModuleInput } from "../module-definition/utils/moduleUtils";
import { standardDate } from "../../../utils/Date";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraTabs } from "../../../components/AtiraTabs";
import { ActivityTab } from "../../../components/activity-timeline/ActivityTab";
import { moduleActions } from "../../../redux/module/module.slice";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import omit from "lodash/omit";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";

const StyledTabs = styled(AtiraTabs)`
  padding: 0;
`;

const StyledSpan = styled.span<{ direction?: Direction }>`
  direction: ${({ direction }) => direction || "auto"};
`;

export const ModuleInstanceReadDrawer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    moduleInstance,
    moduleInstanceReadVisible,
    setModuleInstanceReadVisible,
    setModuleInstance,
  } = useDrawersContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const currentModuleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(
      s,
      moduleInstance?.parentId!,
    ),
  );
  const showLoading = isEmpty(
    omit(moduleInstance, ["_id", "parentId", "entryId"]),
  );

  useEffect(() => {
    if (
      !moduleInstanceReadVisible ||
      !moduleInstance ||
      isEmpty(pick(moduleInstance, ["_id", "parentId", "entryId"]))
    ) {
      return;
    }

    dispatch(
      moduleActions.getModuleInstanceById({
        moduleInstanceId: moduleInstance._id,
        userId: user?._id!,
        moduleDefinitionId: moduleInstance?.parentId!,
        entryId: moduleInstance?.entryId!,
      }),
    ).then((res) => {
      const fetchedModuleInstance = res.payload as BaseModuleInstance;
      if (fetchedModuleInstance) {
        setModuleInstance(fetchedModuleInstance);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleInstance?._id, moduleInstanceReadVisible, user?._id]);

  if (!moduleInstance) {
    return null;
  }

  return (
    <Drawer
      title={t("modules.instance.read.title")}
      open={moduleInstanceReadVisible}
      onClose={() => setModuleInstanceReadVisible(false)}
      size={"large"}
      key={moduleInstance?._id}
    >
      {showLoading ? <SpinnerFullScreen /> : null}

      <StyledTabs
        items={[
          {
            key: "info",
            label: t("common.info"),
            children: (
              <Flex flexDirection="column" gap="s">
                {moduleInstance?.name ? (
                  <Text>
                    <strong>{t("modules.instance.name.title")}: </strong>
                    <span>{moduleInstance?.name}</span>
                  </Text>
                ) : null}

                {Object.entries(moduleInstance?.fields || {}).map(
                  ([key, value]) => {
                    const currentInput = currentModuleDefinition?.inputs?.find(
                      (inp) => inp.name === key,
                    );

                    const direction = [
                      InputTypes.EMAIL,
                      InputTypes.PHONE,
                      InputTypes.NUMBER,
                    ].includes(currentInput?.type!)
                      ? Direction.LTR
                      : undefined;

                    return (
                      <Text key={key}>
                        <strong>{currentInput?.label || key}: </strong>
                        <StyledSpan direction={direction}>
                          {isDateModuleInput(currentModuleDefinition!, key)
                            ? (standardDate(
                                value as string,
                                user,
                                true,
                              ) as string)
                            : (value?.toString() as string)}
                        </StyledSpan>
                      </Text>
                    );
                  },
                )}
              </Flex>
            ),
          },
          {
            key: "activities",
            label: t("common.activities"),
            children: <ActivityTab entity={moduleInstance} />,
          },
        ]}
      />
    </Drawer>
  );
};
